import { Play, Pause } from 'lucide-react';
import { useTimelineStore } from '@/stores/timeline-store';
import { cn } from '@/lib/utils';

/** Selectable playback speeds. */
const SPEEDS = [1, 2, 4] as const;

/** Bottom timeline: LIVE indicator + scrub slider + play/speed (historical). */
export function TimelineBar(): React.JSX.Element {
  const mode = useTimelineStore((s) => s.mode);
  const cursor = useTimelineStore((s) => s.cursor);
  const rangeStart = useTimelineStore((s) => s.rangeStart);
  const rangeEnd = useTimelineStore((s) => s.rangeEnd);
  const playing = useTimelineStore((s) => s.playing);
  const speed = useTimelineStore((s) => s.speed);
  const scrubTo = useTimelineStore((s) => s.scrubTo);
  const togglePlay = useTimelineStore((s) => s.togglePlay);
  const setSpeed = useTimelineStore((s) => s.setSpeed);
  const goLive = useTimelineStore((s) => s.goLive);

  const live = mode === 'live';
  const value = live ? rangeEnd : cursor;

  return (
    <div className="absolute bottom-3 left-1/2 z-[1000] flex w-[min(560px,70vw)] -translate-x-1/2 items-center gap-3 rounded-lg border border-echo-border bg-echo-grey/92 px-3 py-2 shadow-2xl ring-1 ring-black/20 backdrop-blur">
      <LiveButton live={live} onClick={goLive} />
      <button
        type="button"
        onClick={togglePlay}
        disabled={live}
        aria-label={playing ? 'Pause' : 'Play'}
        className="text-muted-foreground transition-colors enabled:hover:text-foreground disabled:opacity-40"
      >
        {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
      </button>
      <span className="w-[68px] shrink-0 font-mono text-[10px] text-muted-foreground">{formatClock(value)}</span>
      <input
        type="range"
        min={rangeStart}
        max={rangeEnd}
        value={value}
        step={1000}
        onChange={(event) => scrubTo(Number(event.target.value))}
        aria-label="Timeline"
        className="h-1 flex-1 cursor-pointer appearance-none rounded bg-echo-border accent-echo-yellow"
      />
      <SpeedControl speed={speed} onSet={setSpeed} disabled={live} />
    </div>
  );
}

/** LIVE pill — pulsing when live; click to return to live. */
function LiveButton({ live, onClick }: { live: boolean; onClick: () => void }): React.JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex shrink-0 items-center gap-1.5 rounded px-2 py-1 font-chakra text-[10px] font-bold uppercase tracking-wider transition-colors',
        live ? 'bg-red-500/20 text-red-400' : 'bg-echo-border/40 text-muted-foreground hover:text-foreground',
      )}
    >
      <span className={cn('h-1.5 w-1.5 rounded-full', live ? 'animate-pulse bg-red-500' : 'bg-muted-foreground')} />
      Live
    </button>
  );
}

/** Playback speed cycler (1×/2×/4×). */
function SpeedControl({
  speed,
  onSet,
  disabled,
}: {
  speed: number;
  onSet: (speed: number) => void;
  disabled: boolean;
}): React.JSX.Element {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => onSet(nextSpeed(speed))}
      className="w-8 shrink-0 rounded bg-echo-border/40 py-1 font-mono text-[10px] text-foreground transition-colors enabled:hover:bg-echo-border/60 disabled:opacity-40"
    >
      {speed}×
    </button>
  );
}

/** Next speed in the cycle. */
function nextSpeed(speed: number): number {
  const index = SPEEDS.indexOf(speed as (typeof SPEEDS)[number]);
  return SPEEDS[(index + 1) % SPEEDS.length];
}

/** Format an epoch-ms value as HH:MM:SS. */
function formatClock(ms: number): string {
  const date = new Date(ms);
  if (Number.isNaN(date.getTime())) return '--:--:--';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' });
}
